import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";

import Select, { components } from "react-select";

import { ReactComponent as Error } from "../../../../assets/icons/error.svg";
import { ReactComponent as RevertApprover } from "../../../../assets/icons/revertApproverModal.svg";

import { ReactComponent as DropdownArrowDown } from "../../../../assets/icons/dropdownArrowDown.svg";
import { ReactComponent as DropdownArrowUp } from "../../../../assets/icons/dropdownArrowUp.svg";
import { sanitizeHtml } from "../../../../shared/sanitize";
import APIVariables from "../../../../services/api-variables";
import { handleCatch } from "../../../../shared/helpers";
import useRoles from "../../../../hooks/useRoles";
import Axios from "../../../../services/Axios";

/** Centered modal: one width rule (no !w / breakpoint %) — narrow on mobile, capped on large screens */
const modalContentClassName =
  "fixed left-1/2 top-1/2 z-5 w-11/12 max-w-2xl max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl bg-white outline-none";

const overlayClassName = "fixed inset-0 z-4 bg-black/20";

const secondTextClassName =
  "py-2 text-left text-base leading-relaxed text-neutral-600 [&_a]:text-neutral-600 [&_a]:underline [&_div]:max-h-40 [&_div]:overflow-y-auto";

const fieldErrorClassName = "block text-left text-sm font-light italic text-red-600";

const getReasonSelectStyles = (hasFieldError) => ({
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    backgroundColor: "transparent",
    borderRadius: 0,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: hasFieldError ? "#e41818" : "#c8c8c8",
    boxShadow: "none",
    minHeight: 42,
    paddingLeft: 10,
    paddingRight: 10,
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  singleValue: (base) => ({
    ...base,
    color: "#646464",
    fontSize: 18,
    lineHeight: "22px",
    textAlign: "left",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#646464",
    fontSize: 17,
    textAlign: "left",
  }),
});

const ConfirmationModal = ({
  statePAFModal,
  closeConfirmationModal,
  message,
  submitError,
  submitValidate,
  handleChangeSelect,
  form,
  revert,
  handleChange,
  handleErrors,
  errors,
  detailsPAF,
}) => {
  const [stateTheReasonOfReject, setStateTheReasonOfReject] = useState("");
  const prescriptionId = window.location.href.split("/")[5];
  const { isHomecare, isOutpatient, isPharmacy } = useRoles();

  const renameKeys = async (toBeRenamed) => {
    await toBeRenamed.forEach((elem) => {
      elem["label"] = elem.value;
      elem["value"] = elem.id;
      delete elem["id"];
    });
    return toBeRenamed;
  };

  useEffect(() => {
    if (
      !isHomecare &&
      !isOutpatient &&
      (detailsPAF[0].approved_or_rejected_by.includes("Awaiting approval") || detailsPAF[0].status.includes("Status Update"))
    ) {
      const getRejectionReasons = async () => {
        const res = await Axios.get(
          APIVariables.PRESCRIPTIONS_REJECT + prescriptionId
        ).catch((error) => {
          handleCatch(error);
        });
        setStateTheReasonOfReject(await renameKeys(res.data.rejection_reason));
      };
      getRejectionReasons();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      message.state === 3 &&
      (isHomecare || isOutpatient || isPharmacy) &&
      !detailsPAF[0].approved_or_rejected_by.includes("Awaiting approval")
    ) {
      const getRevertReasons = async () => {
        const res = await Axios.get(
          APIVariables.PRESCRIPTIONS_REVERT + prescriptionId
        ).catch((error) => {
          handleCatch(error);
        });
        setStateTheReasonOfReject(await renameKeys(res.data.rejection_reason));
      };
      getRevertReasons();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const DropdownIndicator = (props) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          {props.selectProps.menuIsOpen ? (
            <DropdownArrowUp />
          ) : (
            <DropdownArrowDown />
          )}
        </components.DropdownIndicator>
      )
    );
  };

  const titleClassName =
    message.type === "error"
      ? "mb-2.5 text-3xl font-bold text-red-600"
      : message.type === "revert"
        ? "mb-2.5 text-3xl font-bold text-fuchsia-800"
        : "text-3xl font-bold text-green-600";

  const primaryButtonClassName =
    message.type === "error"
      ? "h-12 min-w-40 cursor-pointer rounded-full border-0 bg-red-600 px-6 text-lg font-bold text-white hover:bg-red-500"
      : message.type === "revert"
        ? "h-12 min-w-40 cursor-pointer rounded-full border-0 bg-fuchsia-700 px-6 text-lg font-bold text-white hover:bg-fuchsia-600"
        : "h-12 min-w-40 cursor-pointer rounded-full border-0 bg-green-600 px-6 text-base font-bold text-white hover:bg-green-700";

  return (
    <Modal
      isOpen={statePAFModal}
      onRequestClose={closeConfirmationModal}
      className={modalContentClassName}
      contentLabel="Confirmation Modal"
      overlayClassName={overlayClassName}
      shouldCloseOnOverlayClick={false} // add this to prevent outside click to prevent modal close
    >
      <div
        className="grid min-h-80 gap-4 p-8 text-center sm:p-12"
        data-testid="paf-confirm-modal"
      >
        <div className="mb-2.5 flex justify-center">
          {message.type === "error" ? (
            <Error />
          ) : message.type === "revert" ? (
            <RevertApprover />
          ) : (
            ""
          )}
        </div>
        <div className={titleClassName} data-testid="paf-confirm-title">
          {message.title}
        </div>
        {message.description &&
          (typeof message.description === "object" ? (
            <div
              className={secondTextClassName}
              data-testid="paf-confirm-description"
            >
              {message.description}
            </div>
          ) : (
            <div
              className={secondTextClassName}
              data-testid="paf-confirm-description"
              dangerouslySetInnerHTML={sanitizeHtml(message.description)}
            ></div>
          ))}

        {message.state === 2 && (
          <div
            className="mt-4 min-h-24 text-left"
            data-testid="paf-confirm-rejection-section"
          >
            <div className="flex flex-col gap-2">
              <Select
                className="w-full text-left"
                classNamePrefix="custom-select-error"
                components={{
                  DropdownIndicator: DropdownIndicator,
                  IndicatorSeparator: () => null,
                }}
                value={form.reason_rejection}
                onChange={(selectedOption) => {
                  handleChangeSelect(selectedOption, "reason_rejection");
                }}
                options={stateTheReasonOfReject}
                placeholder="Reason for rejection"
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={getReasonSelectStyles(!!errors.reason_rejection)}
                inputId="paf-confirm-rejection-reason-select"
              />
              {errors.reason_rejection && (
                <span
                  className={fieldErrorClassName}
                  data-testid="paf-confirm-rejection-reason-error"
                >
                  {errors.reason_rejection}
                </span>
              )}
            </div>
            {form?.reason_rejection?.is_rejection_reason_other === true && (
              <div className="mt-5">
                <input
                  className="h-10 w-full border-0 bg-neutral-100 px-2 outline-none"
                  type="text"
                  onChange={(e) => {
                    // Remove emojis using Unicode property escapes
                    const cleanedValue = e.target.value.replace(/\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu, "");
                    handleChange("reason_rejection_input", cleanedValue)
                  }}
                  value={form.reason_rejection_input || ''}
                  name="reason_rejection_input"
                  data-testid="paf-confirm-rejection-reason-input"
                />
                {errors.reason_rejection_input && (
                  <span
                    className={`mt-1 ${fieldErrorClassName}`}
                    data-testid="paf-confirm-rejection-reason-input-error"
                  >
                    {errors.reason_rejection_input}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        {message.state === 3 && (
          <div
            className="mt-4 min-h-24 text-left"
            data-testid="paf-confirm-revert-section"
          >
            <div className="flex flex-col gap-2">
              <Select
                className="w-full text-left"
                classNamePrefix="custom-select-revert"
                components={{
                  DropdownIndicator: DropdownIndicator,
                  IndicatorSeparator: () => null,
                }}
                value={revert.reason_revert}
                onChange={(selectedOption) => {
                  handleChangeSelect(selectedOption, "reason_reverting");
                }}
                options={stateTheReasonOfReject}
                placeholder="Reason for reverting"
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={getReasonSelectStyles(!!errors.reason_revert)}
                inputId="paf-confirm-revert-reason-select"
              />
              {errors.reason_revert && (
                <span
                  className={fieldErrorClassName}
                  data-testid="paf-confirm-revert-reason-error"
                >
                  {errors.reason_revert}
                </span>
              )}
            </div>
            {revert?.reason_revert?.is_revert_reason_other === true && (
              <div className="mt-5">
                <input
                  className="h-10 w-full border-0 bg-neutral-100 px-2 outline-none"
                  type="text"
                  onChange={(e) =>
                    handleChange("reason_revert_input", e.target.value)
                  }
                  name="reason_revert_input"
                  data-testid="paf-confirm-revert-reason-input"
                />
                {errors.reason_revert_input && (
                  <span
                    className={`mt-1 ${fieldErrorClassName}`}
                    data-testid="paf-confirm-revert-reason-input-error"
                  >
                    {errors.reason_revert_input}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className="mb-10 flex flex-wrap items-center justify-center gap-6"
        data-testid="paf-confirm-actions"
      >
        <button
          type="button"
          className="cursor-pointer border-none bg-transparent text-base font-bold text-neutral-600 hover:text-neutral-500"
          onClick={closeConfirmationModal}
          data-testid="paf-confirm-cancel-button"
        >
          Cancel
        </button>
        <button
          type="button"
          className={primaryButtonClassName}
          data-testid="paf-confirm-submit-button"
          onClick={
            message.type === "error" && message.state === 1
              ? submitError
              : message.type === "error" && message.state === 2
                ? handleErrors
                : message.type === "revert" && message.state === 3
                  ? handleErrors
                  : submitValidate
          }
        >
          {message.buttonText}
        </button>
      </div>
    </Modal>
  );
};

ConfirmationModal.propTypes = {
  statePAFModal: PropTypes.bool,
  closeConfirmationModal: PropTypes.func,
  message: PropTypes.object,
  submitError: PropTypes.func,
  submitValidate: PropTypes.func,
  handleChangeSelect: PropTypes.func,
  form: PropTypes.object,
  handleChange: PropTypes.func,
  handleErrors: PropTypes.func,
  errors: PropTypes.object,
};

export default ConfirmationModal;
